/**
 * ============================================================================
 * DIALOGS STORE - UPDATE OPERATIONS
 * ============================================================================
 * 
 * Handles updating existing dialogs via the backend API.
 * 
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Automatic rollback on failure
 * - Translation string management
 * 
 * @module dialogs/api/update
 */

import { apiService } from "@/lib/api-functions/api-service";
import { createString, updateStringsItem } from "../../stringsStore";
import { dialogStore } from "../store";
import { processDialogForTranslation } from "../utils/translation";
import type { Dialog, UpdateDialogData, ToastFunction } from "../types";

// ============================================================================
// UPDATE OPERATIONS
// ============================================================================

/**
 * Update an existing dialog via API and sync the store.
 * 
 * Features:
 * - Performs optimistic update for instant UI feedback
 * - Automatically rolls back on API failure
 * - Processes translatable fields (creates new strings, updates existing)
 * 
 * @param dialog - The dialog data to update (must include id)
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to the updated dialog
 * 
 * @example
 * ```ts
 * const updated = await updateDialog({
 *   id: 'dialog-123',
 *   game_id: 'game-456',
 *   name: 'Updated Dialog',
 *   dialog_key: 'tutorial_intro',
 *   data: { sections: [...] },
 * });
 * ```
 */
export async function updateDialog(
  dialog: UpdateDialogData,
  toast?: ToastFunction
): Promise<Dialog> {
  // Store the current state for potential rollback
  const currentData = dialogStore.getData();
  const existingDialog = currentData.find(d => d.id === dialog.id);
  
  // ========================================================================
  // OPTIMISTIC UPDATE
  // ========================================================================
  if (existingDialog) {
    const optimisticDialog: Dialog = {
      ...existingDialog,
      ...dialog,
      updated_at: new Date().toISOString(),
    };
    
    const optimisticData = currentData.map(d => 
      d.id === dialog.id ? optimisticDialog : d
    );
    dialogStore.setData(optimisticData);
  }

  try {
    // Process translatable fields and options to create/update strings
    const { dialog: processedDialog } = await processDialogForTranslation(
      dialog,
      createString,
      updateStringsItem
    );
    
    // ======================================================================
    // SYNC string_ids from processed sections to nodes
    // processDialogForTranslation only touches sections, but the backend
    // uses nodes as source of truth. Without this sync, the backend would
    // see nodes without string_id and create duplicate strings.
    // ======================================================================
    if (processedDialog.data?.nodes && processedDialog.data?.sections) {
      // Build a map of field id → { string_id, options } from processed sections
      const fieldStringIdMap = new Map<string, string>();
      const fieldOptionsMap = new Map<string, any[]>();
      for (const section of processedDialog.data.sections) {
        if (section.fields) {
          for (const field of section.fields) {
            if (field.string_id) {
              fieldStringIdMap.set(field.id, field.string_id);
            }
            // Also track options with string_ids for syncing to nodes
            if (field.options && field.options.length > 0) {
              fieldOptionsMap.set(field.id, field.options);
            }
          }
        }
      }
      // Apply string_ids to matching nodes (nodes share IDs with fields)
      if (fieldStringIdMap.size > 0 || fieldOptionsMap.size > 0) {
        processedDialog.data.nodes = processedDialog.data.nodes.map((node: any) => {
          let updatedNode = node;
          // Sync field-level string_id
          const stringId = fieldStringIdMap.get(node.id);
          if (stringId && !node.string_id) {
            updatedNode = { ...updatedNode, string_id: stringId };
          }
          // Sync option-level string_ids
          const processedOptions = fieldOptionsMap.get(node.id);
          if (processedOptions && node.options) {
            const updatedOptions = node.options.map((nodeOpt: any, idx: number) => {
              const processedOpt = processedOptions[idx];
              if (processedOpt?.string_id && !nodeOpt.string_id) {
                return { ...nodeOpt, string_id: processedOpt.string_id };
              }
              return nodeOpt;
            });
            updatedNode = { ...updatedNode, options: updatedOptions };
          }
          return updatedNode;
        });
      }
    }
    
    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // ======================================================================
    // SEND TO API
    // ======================================================================
    const result = await apiService.updateDialog(processedDialog);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update dialog');
    }

    const updatedDialog = result.data;
    
    // ======================================================================
    // SYNC STORE WITH SERVER RESPONSE
    // ======================================================================
    const finalData = dialogStore.getData();
    const updatedData = finalData.map(d => 
      d.id === dialog.id ? updatedDialog : d
    );
    dialogStore.setData(updatedData);

    return updatedDialog;
    
  } catch (err) {
    // ======================================================================
    // ROLLBACK ON FAILURE
    // ======================================================================
    dialogStore.setData(currentData);
    
    const message = err instanceof Error ? err.message : 'Failed to update dialog';
    console.error('[updateDialog] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
